import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import {
  CheckoutDeliveryAddressService,
  CheckoutDeliveryAddressConnector
} from '@spartacus/checkout/base/core'
import {
  CheckoutDeliveryModesFacade,
  CheckoutQueryFacade,
  CheckoutState,
  CheckoutSupportedDeliveryModesQueryResetEvent
} from '@spartacus/checkout/base/root'
import {
  Address,
  Command,
  CommandService,
  CommandStrategy,
  EventService,
  UserActions,
  UserAddressService,
  UserIdService
} from '@spartacus/core'
import {combineLatest, Observable, Subscription } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { EbCheckoutCommandService } from '../../commands/eb-checkout-command.service'
import { EbAddress } from '../../interface/eb-checkout.interface'
import { EbCheckoutAdapter } from '../connectors/eb-checkout.adapter'

@Injectable({ providedIn: 'root' })
export class EbCheckoutDeliveryService extends CheckoutDeliveryAddressService {
  constructor(
    activeCartService: ActiveCartFacade,
    userIdService: UserIdService,
    eventService: EventService,
    commandService: CommandService,
    checkoutDeliveryAddressConnector: CheckoutDeliveryAddressConnector,
    checkoutQueryFacade: CheckoutQueryFacade,
    protected checkoutAdapter: EbCheckoutAdapter,
    protected userAddressService: UserAddressService,
    protected checkoutStore: Store<CheckoutState>,
    protected ebCheckoutCommandService: EbCheckoutCommandService,
    protected checkoutDeliveryModesFacade: CheckoutDeliveryModesFacade,
  ) {
    super(
      activeCartService,
      userIdService,
      eventService,
      commandService,
      checkoutDeliveryAddressConnector,
      checkoutQueryFacade
    )
  }

  /**
   * Set delivery address
   * @param deliveryAddress : Address to be set
   */
  override setDeliveryAddress(deliveryAddress: Address) {
    return this.setDeliveryAddressCommand.execute(deliveryAddress)
  }

  /**
   * Set requested shipping date
   * @param date shipping date to be set
   * @param isValidationNeeded flag for validation
   * @param changedByCustomer flag for manually change
   * @param recalculateCart flag for cart recalculation
   */
  setRequestedShippingDate(
    date: string,
    isValidationNeeded: boolean,
    changedByCustomer: boolean,
    recalculateCart?: boolean,
  ):Subscription {
    return combineLatest([this.userIdService.getUserId(), this.activeCartFacade.getActiveCartId()])
      .pipe(
        filter(([userId, cartId]) => !!(userId && cartId)),
        take(1)
      )
      .subscribe(([userId, cartId]) => {
        this.ebCheckoutCommandService.setRequestedShippingDate(
          cartId,
          userId,
          date,
          isValidationNeeded,
          changedByCustomer,
          recalculateCart
        )
      })
  }

  /**
   * Set delivery mode type
   * @param completedDelivery : boolean to define delivery mode type
   */
  setCompleteDelivery(completedDelivery: boolean): void {
    combineLatest([this.userIdService.getUserId(), this.activeCartFacade.getActiveCartId()])
      .pipe(
        filter(([userId, cartId]) => !!(userId && cartId)),
        take(1)
      )
      .subscribe(([userId, cartId]) => {
        this.ebCheckoutCommandService.setCompleteDeliveryCommand.execute({
          cartId,
          userId,
          completedDelivery
        })
      })
  }

  submitEditAddressForm(addressId: string, attentionOfText: string): Observable<any> {
    return combineLatest([
      this.userIdService.getUserId(),
      this.activeCartFacade.getActiveCartId()
    ]).pipe(
      filter(([userId, cartId]) => !!(userId && cartId)),
      take(1),
      switchMap(([userId, cartId]) =>
        this.checkoutAdapter.setShippingAddressChange(userId, cartId, addressId, attentionOfText)
      ),
      map(() => {
        this.userAddressService.loadAddresses()
        return this.userAddressService.getAddresses().pipe(
          map(addresses => {
            this.checkoutStore.dispatch(new UserActions.AddUserAddressSuccess(addresses))
          })
        )
      })
    )
  }

  submitNewAddressForm(newAddress: EbAddress): Observable<any> {
    return combineLatest([
      this.userIdService.getUserId(),
      this.activeCartFacade.getActiveCartId()
    ]).pipe(
      filter(([userId, cartId]) => !!(userId && cartId)),
      take(1),
      switchMap(([userId, cartId]) =>
        this.checkoutAdapter.addNewEbCommerceAddress(userId, cartId, newAddress)
      ),
      map(() => {
        this.userAddressService.loadAddresses()
        return this.userAddressService.getAddresses().pipe(
          map(addresses => {
            this.checkoutStore.dispatch(new UserActions.AddUserAddressSuccess(addresses))
          })
        )
      })
    )
  }

  submitEditCommerceAddressForm(addressId: string, editedAddress: EbAddress): Observable<any> {
    return combineLatest([
      this.userIdService.getUserId(),
      this.activeCartFacade.getActiveCartId()
    ]).pipe(
      filter(([userId, cartId]) => !!(userId && cartId)),
      take(1),
      switchMap(([userId, cartId]) =>
        this.checkoutAdapter.editEbCommerceAddress(userId, addressId, cartId, editedAddress)
      ),
      map(() => {
        this.userAddressService.loadAddresses()
        return this.userAddressService.getAddresses().pipe(
          map(addresses => {
            this.checkoutStore.dispatch(new UserActions.AddUserAddressSuccess(addresses))
          })
        )
      })
    )
  }
  submitDeleteAddress(addressId: string): Observable<any>{
    return combineLatest([
      this.userIdService.getUserId(),
    ]).pipe(
      filter(([userId]) => !!(userId)),
      take(1),
      switchMap(([userId]) =>
        this.checkoutAdapter.deleteEbCommerceAddress( addressId, userId )
      ),
      map(() => {
        this.userAddressService.loadAddresses()
        return this.userAddressService.getAddresses().pipe(
          map(addresses => {
            this.checkoutStore.dispatch(new UserActions.AddUserAddressSuccess(addresses))
          })
        )
      })
    )
  }

  protected override setDeliveryAddressCommand: Command<Address> =
    this.commandService.create<Address>(
      address =>
        this.checkoutPreconditions().pipe(
          switchMap(([userId, cartId]) => {
            const addressId = address.id
            if (!addressId) {
              throw new Error('Checkout conditions not met')
            }
            return this.checkoutDeliveryAddressConnector.setAddress(userId, cartId, addressId).pipe(
              switchMap(() => this.checkoutDeliveryModesFacade.clearCheckoutDeliveryMode()),
              tap(() => {
                this.eventService.dispatch(
                  {
                    userId,
                    cartId
                  },
                  CheckoutSupportedDeliveryModesQueryResetEvent
                )
              })
            )
          })
        ),
      {
        strategy: CommandStrategy.CancelPrevious
      }
    )

  protected override checkoutPreconditions(): Observable<[string, string]> {
    return combineLatest([
      this.userIdService.getUserId(),
      this.activeCartFacade.getActiveCartId()
    ]).pipe(
      filter(([userId, cartId]) => !!(userId && cartId)),
      take(1),
      map(([userId, cartId]) => [userId, cartId])
    )
  }
}
